import type { MarketId, TemplateView } from '@ps/isp-web-services-types';
import React from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '@swf/core/store';
import { EventTemplate } from '@swf/sports/components/EventTemplate/EventTemplate';
import FormattedDateTime from '@swf/sports/components/FormattedDateTime/FormattedDateTime';
import { MarketTemplate } from '@swf/sports/components/MarketTemplate/MarketTemplate';
import { useEventMarketTemplatesWidget } from '@swf/sports/models/market-templates/eventBased';
import { isEventTemplate } from '@swf/sports/types/templates';
import { isCompoundMarketTemplate } from '@swf/sports/utils/isCompoundMarketTemplate';

import useGetMarket from '../utils/useGetMarket';

import styles from './DatePanel.scss';

interface Props {
  templates: TemplateView[];
  isFirstDateInCompetition: boolean;
  marketId?: MarketId;
  marketIds?: MarketId[];
}

const DatePanel: React.FC<Props> = ({
  templates,
  marketId,
  marketIds,
  isFirstDateInCompetition
}) => {
  const { widgetSelect } = useEventMarketTemplatesWidget();
  const market = useGetMarket({ marketId, marketIds });
  const firstTemplate = templates[0].template;
  const eventId = isEventTemplate(firstTemplate) ? firstTemplate.eventId : market?.eventId;
  const event = useSelector((state: RootState) =>
    widgetSelect.event(state, market ? market.eventId : eventId ?? -1)
  );
  const eventDate = event.eventStartTime;

  return (
    <div data-testid="event-list">
      <div className={styles.dividerText}>
        <FormattedDateTime date={eventDate} hideTime format="long" weekday="long" />
      </div>

      <ol className={styles.list}>
        {templates.map(({ template }, index) => {
          if (isEventTemplate(template)) {
            return (
              <EventTemplate
                eventId={template.eventId}
                templateId={template.templateId}
                key={template.eventId}
              />
            );
          }

          const templateMarketId = isCompoundMarketTemplate(template)
            ? template.marketIds[0]
            : template.marketId;

          return (
            <MarketTemplate
              {...template}
              key={templateMarketId}
              templateDisplayConfig={
                isCompoundMarketTemplate(template)
                  ? { EVENT_TABLE: { hideHeader: !isFirstDateInCompetition || index > 0 } }
                  : undefined
              }
            />
          );
        })}
      </ol>
    </div>
  );
};

export default DatePanel;
